import { 
  createDocument, 
  queryDocuments, 
  updateDocument, 
  getDocument
} from './firebase';
import { serverTimestamp } from 'firebase/firestore';
import { getCurrentUser } from './firebase';

const LEADS_COLLECTION = 'leads';

// Lead types for categorization
export const LEAD_TYPES = {
  CONTACT: 'contact',
  QUOTE: 'quote',
  CALCULATOR: 'calculator',
  NEWSLETTER: 'newsletter',
  PHONE: 'phone'
};

// Lead statuses
export const LEAD_STATUS = {
  NEW: 'new',
  CONTACTED: 'contacted',
  QUALIFIED: 'qualified',
  CONVERTED: 'converted',
  CLOSED: 'closed'
};

/**
 * Create a new lead in Firestore
 */
export const createLead = async (leadData) => {
  try {
    const currentUser = getCurrentUser();
    
    const leadWithMetadata = {
      ...leadData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: LEAD_STATUS.NEW,
      userId: currentUser?.uid || null,
      userEmail: currentUser?.email || leadData.email,
      source: leadData.source || 'website',
      priority: determinePriority(leadData),
      tags: generateTags(leadData)
    };

    const docId = await createDocument(LEADS_COLLECTION, leadWithMetadata);
    return { id: docId, ...leadWithMetadata };
  } catch (error) {
    console.error('Error creating lead:', error);
    throw error;
  }
};

/**
 * Get leads with optional filtering
 */
export const getLeads = async (filters = {}) => {
  try {
    const conditions = [];
    
    if (filters.status) {
      conditions.push({ field: 'status', operator: '==', value: filters.status });
    }
    
    if (filters.type) {
      conditions.push({ field: 'type', operator: '==', value: filters.type });
    }
    
    if (filters.userId) {
      conditions.push({ field: 'userId', operator: '==', value: filters.userId });
    }

    const orderBy = { field: 'createdAt', direction: 'desc' };
    
    const leads = await queryDocuments(LEADS_COLLECTION, conditions, orderBy);
    return leads.map(lead => ({
      ...lead,
      createdAt: lead.createdAt?.toDate(),
      updatedAt: lead.updatedAt?.toDate()
    }));
  } catch (error) {
    console.error('Error getting leads:', error);
    return [];
  }
};

/**
 * Update lead status
 */
export const updateLeadStatus = async (leadId, status, notes = '') => {
  try {
    const updateData = {
      status,
      updatedAt: serverTimestamp()
    };
    
    if (notes) {
      updateData.notes = notes;
    }
    
    await updateDocument(LEADS_COLLECTION, leadId, updateData);
  } catch (error) {
    console.error('Error updating lead status:', error);
    throw error;
  }
};

/**
 * Get lead by ID
 */
export const getLead = async (leadId) => {
  try {
    const lead = await getDocument(LEADS_COLLECTION, leadId);
    if (lead) {
      return {
        ...lead,
        createdAt: lead.createdAt?.toDate(),
        updatedAt: lead.updatedAt?.toDate()
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting lead:', error);
    throw error;
  }
};

/**
 * Determine lead priority based on data
 */
function determinePriority(leadData) {
  // High priority for specific keywords or large installations
  const highPriorityKeywords = ['urgent', 'asap', 'commercial', 'large'];
  const message = (leadData.message || '').toLowerCase();
  
  if (highPriorityKeywords.some(keyword => message.includes(keyword))) {
    return 'high';
  }
  
  // High priority for large solar installations
  if (leadData.numPanels && parseInt(leadData.numPanels) > 20) {
    return 'high';
  }
  
  // Medium priority for calculator leads with specific data
  if (leadData.type === LEAD_TYPES.CALCULATOR && leadData.numPanels) {
    return 'medium';
  }
  
  return 'normal';
}

/**
 * Generate tags for better categorization
 */
function generateTags(leadData) {
  const tags = [];
  
  // Add type-based tags
  if (leadData.type) {
    tags.push(leadData.type);
  }
  
  // Add location-based tags
  if (leadData.location) {
    tags.push(`location:${leadData.location.toLowerCase()}`);
  }
  
  // Add installation type tags
  if (leadData.installationType) {
    tags.push(`installation:${leadData.installationType}`);
  }
  
  // Add size-based tags
  if (leadData.numPanels) {
    const numPanels = parseInt(leadData.numPanels);
    if (numPanels <= 10) {
      tags.push('small-system');
    } else if (numPanels <= 20) {
      tags.push('medium-system');
    } else {
      tags.push('large-system');
    }
  }
  
  return tags;
}

/**
 * Get lead analytics/statistics
 */
export const getLeadAnalytics = async () => {
  try {
    const allLeads = await getLeads();
    
    const analytics = {
      total: allLeads.length,
      byStatus: {},
      byType: {},
      byMonth: {},
      conversionRate: 0
    };
    
    // Calculate status distribution
    Object.values(LEAD_STATUS).forEach(status => {
      analytics.byStatus[status] = allLeads.filter(lead => lead.status === status).length;
    });
    
    // Calculate type distribution
    Object.values(LEAD_TYPES).forEach(type => {
      analytics.byType[type] = allLeads.filter(lead => lead.type === type).length;
    });
    
    // Calculate conversion rate
    const convertedLeads = analytics.byStatus[LEAD_STATUS.CONVERTED] || 0;
    analytics.conversionRate = allLeads.length > 0 ? (convertedLeads / allLeads.length * 100).toFixed(2) : 0;
    
    return analytics;
  } catch (error) {
    console.error('Error getting lead analytics:', error);
    return {
      total: 0,
      byStatus: {},
      byType: {},
      byMonth: {},
      conversionRate: 0
    };
  }
};
